import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { usePersona, type AudiencePersona } from "@/contexts/persona-context";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Target,
  Plus,
  Edit,
  Trash2,
  Check,
  Users,
  Calendar,
  DollarSign,
  Heart,
  MapPin,
  Music,
  Building2,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import Navbar from "@/components/navbar";

// Persona Card Component
function PersonaCard({
  persona,
  isActive,
  onApply,
  onDelete,
}: {
  persona: AudiencePersona;
  isActive: boolean;
  onApply: () => void;
  onDelete: () => void;
}) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Build attribute stats with specific colors for each type
  const attributeStats = [
    {
      label: 'age ranges',
      count: persona.ageRanges?.length || 0,
      icon: Calendar,
      iconColor: 'text-blue-500'
    },
    {
      label: 'genders',
      count: persona.genders?.length || 0,
      icon: Users,
      iconColor: 'text-purple-500'
    },
    {
      label: 'ethnicities',
      count: persona.ethnicities?.length || 0,
      icon: Users,
      iconColor: 'text-orange-500'
    },
    {
      label: 'income levels',
      count: persona.incomeLevels?.length || 0,
      icon: DollarSign,
      iconColor: 'text-green-500'
    },
    {
      label: 'locations',
      count: persona.targetLocations?.length || 0,
      icon: MapPin,
      iconColor: 'text-red-500'
    },
    {
      label: 'music genres',
      count: persona.musicGenres?.length || 0,
      icon: Music,
      iconColor: 'text-indigo-500'
    },
    {
      label: 'lifestyle attributes',
      count: persona.lifestyleAttributes?.length || 0,
      icon: Heart,
      iconColor: 'text-pink-500'
    },
  ].filter(stat => stat.count > 0);

  return (
    <>
      <Card className={`group relative overflow-hidden transition-all duration-300 hover:shadow-lg ${isActive ? 'ring-2 ring-green-500 shadow-green-100' : ''}`}>
        {isActive && (
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-green-400 to-green-600" />
        )}
        <CardContent className="p-5">
          {/* Header */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              <span className="text-2xl">{(persona as any).icon || '🎯'}</span>
              <div>
                <h3 className="font-semibold text-neutral-900">{persona.profileName}</h3>
                {(persona as any).brandName && (
                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <Building2 className="h-3 w-3" />
                    {(persona as any).brandName}
                  </div>
                )}
              </div>
            </div>
            {isActive && (
              <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                Active
              </Badge>
            )}
          </div>

          {/* Description */}
          {persona.description && (
            <p className="text-sm text-gray-600 mb-4 line-clamp-2">
              {persona.description}
            </p>
          )}

          {/* Attribute Stats Grid */}
          {attributeStats.length > 0 && (
            <div className="grid grid-cols-2 gap-2 mb-4">
              {attributeStats.map(({ label, count, icon: Icon, iconColor }) => (
                <div key={label} className="flex items-center gap-2 text-sm text-gray-600">
                  <Icon className={`h-4 w-4 ${iconColor}`} />
                  <span>{count} {label}</span>
                </div>
              ))}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2 pt-3 border-t">
            <Link href={`/persona/${persona.id}`} className="flex-1">
              <Button variant="outline" size="sm" className="w-full">
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
            </Link>
            {!isActive && (
              <Button
                variant="default"
                size="sm"
                onClick={onApply}
                className="flex-1"
              >
                <Target className="h-4 w-4 mr-2" />
                Apply
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowDeleteDialog(true)}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 px-2"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Persona</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{persona.profileName}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// Landing page for unauthenticated users
function PersonasLandingPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-white to-neutral-50">
      <Navbar />
      <div className="container mx-auto px-4 py-16 pt-24 max-w-6xl">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full mb-6">
            <Target className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-archivo font-bold text-neutral-900 mb-6">
            Target Audience Personas
          </h1>
          <p className="text-xl text-neutral-600 mb-8 max-w-3xl mx-auto leading-relaxed">
            Create and manage detailed audience personas to filter and match sponsorship opportunities.
            Define demographics, interests, and target markets to find the perfect fit for your brand.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          <div className="text-center p-6 bg-white rounded-xl shadow-sm border border-neutral-100 hover:shadow-lg transition-all duration-300">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg mb-4">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">Demographic Targeting</h3>
            <p className="text-neutral-600 text-sm">
              Define age ranges, gender, income levels, and ethnicity to match your ideal customer profile.
            </p>
          </div>

          <div className="text-center p-6 bg-white rounded-xl shadow-sm border border-neutral-100 hover:shadow-lg transition-all duration-300">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-lg mb-4">
              <MapPin className="h-6 w-6 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">Geographic Targeting</h3>
            <p className="text-neutral-600 text-sm">
              Select specific DMA markets to focus your sponsorship efforts in the regions that matter most.
            </p>
          </div>

          <div className="text-center p-6 bg-white rounded-xl shadow-sm border border-neutral-100 hover:shadow-lg transition-all duration-300">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-purple-100 rounded-lg mb-4">
              <Heart className="h-6 w-6 text-purple-600" />
            </div>
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">Lifestyle & Interests</h3>
            <p className="text-neutral-600 text-sm">
              Target audiences based on lifestyle attributes and music genre preferences.
            </p>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center bg-gradient-to-r from-blue-500/5 via-purple-500/5 to-blue-500/5 rounded-2xl p-8 border border-blue-500/10">
          <h2 className="text-3xl font-bold text-neutral-900 mb-4">Ready to Find Your Perfect Audience?</h2>
          <p className="text-lg text-neutral-600 mb-6 max-w-2xl mx-auto">
            Create a <strong>free account</strong> to start building audience personas and discovering
            sponsorship opportunities that align with your brand.
          </p>
          <div className="flex justify-center gap-4">
            <Link href="/register">
              <Button className="px-8 py-3 text-lg font-semibold">
                Create Free Account
              </Button>
            </Link>
          </div>
          <p className="text-sm text-neutral-500 mt-4">
            Already have an account? <Link href="/login" className="text-primary hover:underline font-medium">Sign in here</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default function PersonasPage() {
  const { user, isLoading: authLoading } = useAuth();
  const { activePersona, savedPersonas, setActivePersona, deletePersona, isLoading: personasLoading } = usePersona();

  // Show loading state while auth is being checked
  if (authLoading) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <Navbar />
        <div className="container mx-auto px-4 py-8 max-w-6xl">
          <div className="animate-pulse">
            <div className="h-16 bg-neutral-200 rounded mb-8" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(3)].map((_, index) => (
                <div key={index} className="bg-white rounded-lg shadow-sm p-6">
                  <div className="h-10 w-10 bg-neutral-200 rounded-full mb-4" />
                  <div className="h-4 bg-neutral-200 rounded mb-2 w-3/4" />
                  <div className="h-3 bg-neutral-200 rounded w-1/2" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show landing page for unauthenticated users
  if (!user) {
    return <PersonasLandingPage />;
  }

  const isLoading = personasLoading;

  const handleApplyPersona = (persona: AudiencePersona) => {
    setActivePersona(persona);
  };

  const handleDeletePersona = async (persona: AudiencePersona) => {
    await deletePersona(persona.id);
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <Navbar />

      {/* Sticky Action Bar */}
      <div className="sticky top-16 z-30 bg-white/95 backdrop-blur-sm border-b border-neutral-200 shadow-sm">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Target className="h-5 w-5 text-primary" />
              <h1 className="text-xl font-semibold text-neutral-900">My Personas</h1>
              {savedPersonas.length > 0 && (
                <Badge variant="secondary">{savedPersonas.length} persona{savedPersonas.length !== 1 ? 's' : ''}</Badge>
              )}
            </div>
            <Link href="/persona/new">
              <Button className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Create Persona
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {isLoading ? (
          // Loading skeleton
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(3)].map((_, index) => (
              <div key={index} className="animate-pulse">
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="h-10 w-10 bg-neutral-200 rounded-full" />
                    <div className="flex-1">
                      <div className="h-4 bg-neutral-200 rounded mb-2 w-3/4" />
                      <div className="h-3 bg-neutral-200 rounded w-1/2" />
                    </div>
                  </div>
                  <div className="h-4 bg-neutral-200 rounded mb-2" />
                  <div className="h-4 bg-neutral-200 rounded w-2/3 mb-4" />
                  <div className="flex gap-2">
                    <div className="h-6 bg-neutral-200 rounded-full w-16" />
                    <div className="h-6 bg-neutral-200 rounded-full w-20" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : savedPersonas.length === 0 ? (
          // Empty state
          <Card>
            <CardContent className="py-12">
              <div className="text-center">
                <Target className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-neutral-900 mb-2">No personas yet</h3>
                <p className="text-neutral-600 mb-6 max-w-md mx-auto">
                  Create your first audience persona to start filtering sponsorship opportunities
                  by demographics, location, and interests.
                </p>
                <Link href="/persona/new">
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Your First Persona
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        ) : (
          // Persona grid
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {savedPersonas.map((persona) => (
              <PersonaCard
                key={persona.id}
                persona={persona}
                isActive={activePersona?.id === persona.id}
                onApply={() => handleApplyPersona(persona)}
                onDelete={() => handleDeletePersona(persona)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
